import React, { useEffect, useState } from "react";
import styles from "./project.module.css";

/**
 * A generic project component
 * @module ProjectComponent
 @prop {String} [projectName] : project title
 @prop {String} [number] : project number
 @prop {boolean} [right] : project image is on the right side
 @prop {String} [projectDescription] : project description
 @prop {String} [githubLink] : link to github repository
 @prop {String} [deployLink] : link to deployed project
 @prop {String} [figmaLink] : link to figma design
 @prop {String} [itchLink] : link to itch.io page
 @prop {String} [image] : project image
 @prop {boolean} [showPython] : python was used in the project
 @prop {boolean} [showDjango] : django was used in the project
 @prop {boolean} [showReact] : react.js was used in the project
 @prop {boolean} [showNode] : node.js was used in the project
 @prop {boolean} [showMongoDB] : mongoDB was used in the project
 @prop {boolean} [showExpress] : express.js was used in the project
 @prop {boolean} [showDart] : dart was used in the project
 @prop {boolean} [showFirebase] : firebase was used in the project
 @prop {boolean} [showHTMLCSS] : html/css was used in the project
 @prop {boolean} [showSQLite] : sqlite was used in the project
 @prop {boolean} [showFigma] : figma was used in the project
 @prop {boolean} [showCSharp] : C# was used in the project
 @prop {boolean} [showUnity] : Unity was used in the project
**/

function Project(props) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Ensure `right` is always false on mobile
    const isImageOnRight = isMobile ? false : props.right;

    return (
        <div className={styles.projectContainer}>
            {!isImageOnRight && (
                <div className={styles.projectImage}>
                    <img
                        src={props.image}
                        class={styles.image}
                        alt="project"
                    />
                </div>)
            }
            {!isImageOnRight && (
                <div className={styles.projectDetailsLeft}>
                    <div className={styles.projectName}>
                        <p className={styles.number}>{props.number}</p>
                        <p className={styles.title}>{props.projectName}</p>
                    </div>
                    <div className={styles.framework}>
                        {props.showHTMLCSS && (
                            <div className={styles.frameworkItemRose}>
                                <p>HTML/CSS</p>
                            </div>
                        )}
                        {props.showReact && (
                            <div className={styles.frameworkItemBlue}>
                                <p>React</p>
                            </div>
                        )}
                        {props.showPython && (
                            <div className={styles.frameworkItemPurple}>
                                <p>Python</p>
                            </div>
                        )}
                        {props.showDjango && (
                            <div className={styles.frameworkItemPurple}>
                                <p>Django</p>
                            </div>
                        )}
                        {props.showNode && (
                            <div className={styles.frameworkItemLime}>
                                <p>Node</p>
                            </div>
                        )}
                        {props.showExpress && (
                            <div className={styles.frameworkItemAmber}>
                                <p>Express</p>
                            </div>
                        )}
                        {props.showMongoDB && (
                            <div className={styles.frameworkItemGreen}>
                                <p>MongoDB</p>
                            </div>
                        )}
                        {props.showDart && (
                            <div className={styles.frameworkItemIndigo}>
                                <p>Dart</p>
                            </div>
                        )}
                        {props.showFirebase && (
                            <div className={styles.frameworkItemAmber}>
                                <p>Firebase</p>
                            </div>
                        )}
                        {props.showSQLite && (
                            <div className={styles.frameworkItemIndigo}>
                                <p>SQLite</p>
                            </div>
                        )}
                        {props.showFigma && (
                            <div className={styles.frameworkItemRose}>
                                <p>Figma</p>
                            </div>
                        )}
                        {props.showCSharp && (
                            <div className={styles.frameworkItemIndigo}>
                                <p>C#</p>
                            </div>
                        )}
                        {props.showUnity && (
                            <div className={styles.frameworkItemRose}>
                                <p>Unity</p>
                            </div>
                        )}
                    </div>
                    <div className={styles.projectDescription}>
                        <p className={styles.description}>{props.projectDescription}</p>
                    </div>
                    <div className={styles.links}>
                        {props.figmaLink && (
                            <div className={styles.continuous}>
                                <a href={props.figmaLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}>
                                    Figma
                                </a>
                            </div>
                        )}
                        {props.githubLink && (
                            <div className={styles.continuous}>
                                <a href={props.githubLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}>
                                    Github
                                </a>
                            </div>
                        )}
                        {props.deployLink && (
                            <div className={styles.continuous}>
                                <a href={props.deployLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}>
                                    Deploy
                                </a>
                            </div>
                        )}
                        {props.itchLink && (
                            <div className={styles.continuous}>
                                <a href={props.itchLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}>
                                    Itch.io
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )
            }
            {isImageOnRight && (
                <div className={styles.projectDetailsRight}>
                    <div className={styles.projectName}>
                        <p className={styles.number}>{props.number}</p>
                        <p className={styles.title}>{props.projectName}</p>
                    </div>
                    <div className={styles.framework}>
                        {props.showHTMLCSS && (
                            <div className={styles.frameworkItemRose}>
                                <p>HTML/CSS</p>
                            </div>
                        )}
                        {props.showReact && (
                            <div className={styles.frameworkItemBlue}>
                                <p>React</p>
                            </div>
                        )}
                        {props.showPython && (
                            <div className={styles.frameworkItemPurple}>
                                <p>Python</p>
                            </div>
                        )}
                        {props.showDjango && (
                            <div className={styles.frameworkItemPurple}>
                                <p>Django</p>
                            </div>
                        )}
                        {props.showNode && (
                            <div className={styles.frameworkItemLime}>
                                <p>Node</p>
                            </div>
                        )}
                        {props.showExpress && (
                            <div className={styles.frameworkItemAmber}>
                                <p>Express</p>
                            </div>
                        )}
                        {props.showMongoDB && (
                            <div className={styles.frameworkItemGreen}>
                                <p>MongoDB</p>
                            </div>
                        )}
                        {props.showDart && (
                            <div className={styles.frameworkItemIndigo}>
                                <p>Dart</p>
                            </div>
                        )}
                        {props.showFirebase && (
                            <div className={styles.frameworkItemAmber}>
                                <p>Firebase</p>
                            </div>
                        )}
                        {props.showSQLite && (
                            <div className={styles.frameworkItemIndigo}>
                                <p>SQLite</p>
                            </div>
                        )}
                        {props.showFigma && (
                            <div className={styles.frameworkItemRose}>
                                <p>Figma</p>
                            </div>
                        )}
                        {props.showCSharp && (
                            <div className={styles.frameworkItemIndigo}>
                                <p>C#</p>
                            </div>
                        )}
                        {props.showUnity && (
                            <div className={styles.frameworkItemRose}>
                                <p>Unity</p>
                            </div>
                        )}
                    </div>
                    <div className={styles.projectDescription}>
                        <p className={styles.description}>{props.projectDescription}</p>
                    </div>
                    <div className={styles.links}>
                        {props.figmaLink && (
                            <div className={styles.continuous}>
                                <a href={props.figmaLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}>
                                    Figma
                                </a>
                            </div>
                        )}
                        {props.githubLink && (
                            <div className={styles.continuous}>
                                <a href={props.githubLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}>
                                    Github
                                </a>
                            </div>
                        )}
                        {props.deployLink && (
                            <div className={styles.continuous}>
                                <a href={props.deployLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}>
                                    Deploy
                                </a>
                            </div>
                        )}
                        {props.itchLink && (
                            <div className={styles.continuous}>
                                <a href={props.itchLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}>
                                    Itch.io
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )
            }
            {isImageOnRight && (
                <div className={styles.projectImage}>
                    <img
                        src={props.image}
                        class={styles.image}
                        alt="project"
                    />
                </div>)
            }
        </div>
    );
}

export default Project;